import { useMemo, useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { DynamicIcon } from "./DynamicIcon";
import { LAUNCH_TYPE_ICONS } from "../lib/launchTypeIcons";
import { IcoSearch } from "./icons";

// Picker открывается как fixed-position popover у якоря на desktop и
// bottom-sheet на <640px (единый проектный паттерн). Поиск — по `label` и
// подстроке `name` (slug). Текущая выбранная иконка подсвечивается.

interface IconPickerProps {
  value: string;
  onPick: (name: string) => void;
  onClose: () => void;
  anchor: HTMLElement | null;
}

export function IconPicker({ value, onPick, onClose, anchor }: IconPickerProps) {
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (isMobile) return;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const width = 360;
    const vw = window.innerWidth;
    const left = Math.min(Math.max(8, rect.left), vw - width - 8);
    setPos({ top: rect.bottom + 6, left, width });
  }, [anchor, isMobile]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onDocClick = (e: MouseEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (root.contains(e.target as Node)) return;
      if (anchor && anchor.contains(e.target as Node)) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDocClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDocClick);
    };
  }, [anchor, onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return LAUNCH_TYPE_ICONS;
    return LAUNCH_TYPE_ICONS.filter(
      (i) => i.name.includes(q) || i.label.toLowerCase().includes(q),
    );
  }, [query]);

  const style: React.CSSProperties = isMobile
    ? { position: "fixed", left: 0, right: 0, bottom: 0, width: "100%" }
    : pos
      ? { position: "fixed", top: pos.top, left: pos.left, width: pos.width }
      : { position: "fixed", top: -9999, left: -9999 };

  return createPortal(
    <div
      ref={rootRef}
      className={"icon-picker" + (isMobile ? " icon-picker-sheet" : "")}
      style={style}
      role="dialog"
      aria-label="Выбрать иконку"
    >
      <div className="icon-picker-head">
        <div className="search" style={{ width: "100%" }}>
          <IcoSearch size={14} stroke="#7A849B" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Поиск иконки…"
          />
        </div>
      </div>
      <div className="icon-picker-grid">
        {filtered.length === 0 ? (
          <div className="icon-picker-empty">Ничего не найдено</div>
        ) : (
          filtered.map((i) => {
            const active = i.name === value;
            return (
              <button
                key={i.name}
                type="button"
                className={"icon-picker-cell" + (active ? " active" : "")}
                onClick={() => {
                  onPick(i.name);
                  onClose();
                }}
                title={i.label}
                aria-label={i.label}
              >
                <DynamicIcon name={i.name} size={20} />
              </button>
            );
          })
        )}
      </div>
    </div>,
    document.body,
  );
}
